import { useCallback, useEffect, useState } from "react";
import { Ban, Radar, Save, Trash2 } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { OUTBOUND_SETTINGS_URL, OUTBOUND_SUPPRESSIONS_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { Btn, inputCls, Section, Toggle } from "../outbound/ui";
import { TEXT, joinSpec, splitSpec, templatePositions } from "../../components/email/LeadWelcomeWhatsAppCard";

/**
 * Settings -> Outbound (trustedge prospecting/). The kill switch, who the
 * emails are from, what the AI writer may say, which facts may be collected,
 * and how fast mail may go. Mail leaves from the mailboxes in the sending pool
 * (Email -> Sending Pool); nothing here adds a mailbox.
 */

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const GROUPS = { reach: "Reach", profile: "Profile", gap: "Gaps we can speak to", intent: "Buying signals" };

function Suppressions() {
    const { fetch, create, remove } = useApiMethods();
    const [rows, setRows] = useState([]);
    const [emails, setEmails] = useState("");
    const load = useCallback(() => {
        fetch(OUTBOUND_SUPPRESSIONS_URL).then(res => { if (res?.status === "success") setRows(res.data); });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [load]);

    const add = async () => {
        const res = await create(OUTBOUND_SUPPRESSIONS_URL, { emails });
        if (res?.status === "success") { ToastMsg("success", res.message); setEmails(""); load(); }
        else ToastMsg("error", res?.message || "Could not add");
    };
    const drop = async (id) => {
        const res = await remove(`${OUTBOUND_SUPPRESSIONS_URL}?id=${id}`);
        if (res?.status === "success") load();
    };

    return (
        <Section title="Never email" subtitle="Unsubscribes and bounces land here on their own. Add competitors, partners, or anyone who asked by phone.">
            <div className="flex gap-2">
                <textarea rows={2} value={emails} onChange={e => setEmails(e.target.value)} placeholder="one address per line" className={cn(inputCls, "max-w-md")} />
                <Btn onClick={add} disabled={!emails.includes("@")}><Ban className="h-3.5 w-3.5" /> Add</Btn>
            </div>
            {rows.length > 0 && (
                <div className="max-h-56 overflow-auto">
                    <table className="w-full text-xs">
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.id} className="border-b border-gray-100 dark:border-gray-700/60">
                                    <td className="py-1 pr-2 text-gray-800 dark:text-gray-100">{r.email}</td>
                                    <td className="py-1 pr-2 text-gray-500">{r.reason}</td>
                                    <td className="py-1 pr-2 text-gray-400">{r.note}</td>
                                    <td className="py-1 pr-2 text-gray-400">{r.created_date}</td>
                                    <td className="py-1 text-right"><button onClick={() => drop(r.id)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </Section>
    );
}

function WhatsAppSection({ d, cfg, set, save, saving }) {
    const fieldKeys = (d.wa_fields || []).map(f => f.key);
    const template = (d.wa_templates || []).find(t => t.id === d.wa_template_id);
    const positions = templatePositions(template);
    const vars = d.wa_variables || {};
    const setVar = (pos, part) => {
        const cur = splitSpec(vars[pos], fieldKeys);
        set("wa_variables", { ...vars, [pos]: joinSpec({ ...cur, ...part }) });
    };
    const templates = (d.wa_templates || []).filter(t => !d.wa_account_id || !t.account_id || t.account_id === d.wa_account_id);
    return (
        <Section title={`WhatsApp to prospects ${cfg.wa_enabled ? "is ON" : "is OFF"}`}
            subtitle="One approved template per prospect, to a 10-digit mobile only, once ever. Numbers come from the map, the website, or the AI web search (Prospects → Find mobile numbers). It sends inside the hours and days below, under its own daily cap. Opening the link makes them an 'AI Research' lead."
            right={<Toggle on={cfg.wa_enabled} disabled={saving || !cfg.is_saved} onClick={() => save({ wa_enabled: !cfg.wa_enabled })} />}>
            <div className="grid gap-3 md:grid-cols-2 text-xs">
                <label className="space-y-1 md:col-span-2"><span className="font-medium">Demo link page</span>
                    <input value={d.landing_url || ""} onChange={e => set("landing_url", e.target.value)} placeholder="https://emedhub.in/go/" className={inputCls} />
                    <span className="text-gray-500 block">Each prospect's own code is added to the end. The page opens the demo booking form; cold emails get the same link as a "Book a free demo" line.</span></label>
                <label className="space-y-1"><span className="font-medium">Send from</span>
                    <select value={d.wa_account_id || ""} onChange={e => set("wa_account_id", e.target.value ? Number(e.target.value) : null)} className={inputCls}>
                        <option value="">Choose a WhatsApp account</option>
                        {(d.wa_accounts || []).map(a => <option key={a.id} value={a.id}>{a.name}{a.phone_number ? ` (${a.phone_number})` : ""}</option>)}
                    </select></label>
                <label className="space-y-1"><span className="font-medium">Template</span>
                    <select value={d.wa_template_id || ""} onChange={e => set("wa_template_id", e.target.value ? Number(e.target.value) : null)} className={inputCls}>
                        <option value="">Choose an approved template</option>
                        {templates.map(t => <option key={t.id} value={t.id}>{t.name} · {t.category}</option>)}
                    </select>
                    {templates.length === 0 && <span className="text-amber-700 block">No approved templates. Sync them under Settings → WA Templates.</span>}</label>
                <label className="space-y-1"><span className="font-medium">WhatsApps per day</span>
                    <input type="number" min={0} max={500} value={d.wa_daily_cap} onChange={e => set("wa_daily_cap", Number(e.target.value) || 0)} className={inputCls} /></label>
                <label className="flex items-center gap-2 md:mt-5">
                    <input type="checkbox" checked={!!d.wa_auto} onChange={e => set("wa_auto", e.target.checked)} />
                    Queue a WhatsApp as soon as a confirmed mobile is found
                </label>
            </div>
            {template && (
                <div className="space-y-2 text-xs">
                    <p className="whitespace-pre-wrap rounded-lg bg-green-50/60 dark:bg-green-900/10 border border-green-100 dark:border-green-900/40 p-2.5 text-gray-800 dark:text-gray-100">{template.body_text}</p>
                    {positions.map(pos => {
                        const v = splitSpec(vars[pos], fieldKeys);
                        return (
                            <div key={pos} className="flex items-center gap-2">
                                <span className="w-10 font-mono text-gray-500">{`{{${pos}}}`}</span>
                                <select aria-label={`Variable ${pos}`} value={v.field} className={cn(inputCls, "flex-1")}
                                    onChange={e => setVar(pos, { field: e.target.value, text: e.target.value === TEXT ? v.text : "" })}>
                                    <option value="">Fill with…</option>
                                    {(d.wa_fields || []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                                    <option value={TEXT}>Fixed text…</option>
                                </select>
                                {v.field === TEXT ? (
                                    <input value={v.text} onChange={e => setVar(pos, { text: e.target.value })} placeholder="Text to send" className={cn(inputCls, "flex-1")} />
                                ) : v.field && !["outbound_link", "outbound_code"].includes(v.field) && (
                                    <input value={v.fallback} onChange={e => setVar(pos, { fallback: e.target.value })} placeholder="If blank, send…" className={cn(inputCls, "flex-1")} />
                                )}
                            </div>
                        );
                    })}
                    <p className="text-gray-500">For a button whose URL ends in a variable (https://emedhub.in/go/{"{{2}}"}) choose <b>Personal link code (button)</b>.</p>
                </div>
            )}
        </Section>
    );
}

export default function OutboundSettings() {
    const { fetch, update } = useApiMethods();
    const [cfg, setCfg] = useState(null);
    const [d, setD] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetch(OUTBOUND_SETTINGS_URL).then(res => {
            if (res?.status === "success") { setCfg(res.data); setD(res.data); }
            else ToastMsg("error", res?.message || "Could not load outbound settings");
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!d) return <ContainerCard><div className="p-6 text-xs text-gray-400">Loading…</div></ContainerCard>;

    const set = (k, v) => setD(x => ({ ...x, [k]: v }));
    const save = async (patch = {}) => {
        setSaving(true);
        try {
            const body = { ...d, ...patch };
            delete body.signal_catalog; delete body.mailboxes;
            delete body.wa_accounts; delete body.wa_templates; delete body.wa_fields;
            const res = await update(OUTBOUND_SETTINGS_URL, body);
            if (res?.status === "success") { ToastMsg("success", "Saved"); setCfg(res.data); setD(res.data); }
            else ToastMsg("error", res?.message || "Could not save");
        } finally { setSaving(false); }
    };
    const strip = (x) => JSON.stringify({ ...x, signal_catalog: 0, mailboxes: 0, wa_accounts: 0, wa_templates: 0, wa_fields: 0 });
    const dirty = strip(cfg) !== strip(d);
    const confirmed = new Set(d.confirmed_signals);
    const toggleSignal = (code) => set("confirmed_signals", confirmed.has(code) ? d.confirmed_signals.filter(c => c !== code) : [...d.confirmed_signals, code]);

    return (
        <ContainerCard>
            <div className="p-4 space-y-5 overflow-auto">
                <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                        <Radar className="h-5 w-5 text-primary" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Outbound</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Cold email to organisations that are not leads yet. Every email still needs approving in the Outbox.</p>
                        </div>
                    </div>
                    <Btn variant="primary" onClick={() => save()} disabled={(!dirty && d.is_saved) || saving}>
                        <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : d.is_saved ? "Save" : "Save and set up"}
                    </Btn>
                </div>

                <Section title={`Sending ${cfg.is_enabled ? "is ON" : "is OFF"}`}
                    subtitle="The kill switch. Off: nothing is sent, ever; discovery, drafts and approvals still work. It saves at once."
                    right={<Toggle on={cfg.is_enabled} disabled={saving || !cfg.is_saved} onClick={() => save({ is_enabled: !cfg.is_enabled })} />}>
                    {!cfg.is_saved && <p className="text-xs text-amber-700">Save these settings once before switching on.</p>}
                </Section>

                <Section title="From" subtitle="Shown in the sign-off and the footer. A postal address is required in every cold email.">
                    <div className="grid gap-3 md:grid-cols-2 text-xs">
                        <label className="space-y-1"><span className="font-medium">Sender name</span>
                            <input value={d.sender_name} onChange={e => set("sender_name", e.target.value)} placeholder="Madhan Kumar" className={inputCls} /></label>
                        <label className="space-y-1"><span className="font-medium">Company</span>
                            <input value={d.company_name} onChange={e => set("company_name", e.target.value)} placeholder="Tecnospice / eMedHub" className={inputCls} /></label>
                        <label className="space-y-1 md:col-span-2"><span className="font-medium">Postal address (footer)</span>
                            <input value={d.postal_address} onChange={e => set("postal_address", e.target.value)} className={inputCls} /></label>
                        <label className="space-y-1"><span className="font-medium">Our website</span>
                            <input value={d.website} onChange={e => set("website", e.target.value)} placeholder="https://emedhub.in" className={inputCls} /></label>
                        <label className="space-y-1"><span className="font-medium">Send from</span>
                            <select value={d.account_id || ""} onChange={e => set("account_id", e.target.value ? Number(e.target.value) : null)} className={inputCls}>
                                <option value="">Sending pool (all enabled mailboxes)</option>
                                {d.mailboxes.map(m => <option key={m.id} value={m.id}>{m.email} · {m.remaining_today}/{m.daily_send_limit} left today</option>)}
                            </select>
                            {d.mailboxes.length === 0 && <span className="text-amber-700">No mailbox can send yet. Enable one in Email → Sending Pool.</span>}
                        </label>
                    </div>
                </Section>

                <WhatsAppSection d={d} cfg={cfg} set={set} save={save} saving={saving} />

                <Section title="What we sell"
                    subtitle="The AI writer uses only this and the facts read from each prospect's site. To fill it from our website, run on the AI Worker PC: python manage.py train_outbound_knowledge --tenant <slug> --url <our site>">
                    <textarea rows={10} value={d.product_knowledge} onChange={e => set("product_knowledge", e.target.value)} className={inputCls}
                        placeholder={"## What we sell\neMedHub hospital management software...\n\n## Who it is for\n...\n\n## Proof\n...\n\n## Offer\nA 15-minute demo"} />
                    <div className="grid gap-3 md:grid-cols-2 text-xs">
                        <label className="space-y-1"><span className="font-medium">Never use these phrases (one per line)</span>
                            <textarea rows={6} value={d.ban_phrases} onChange={e => set("ban_phrases", e.target.value)} className={inputCls} /></label>
                        <div className="space-y-2">
                            <label className="space-y-1 block"><span className="font-medium">Writer model</span>
                                <select value={d.writer_model} onChange={e => set("writer_model", e.target.value)} className={cn(inputCls, "w-40")}>
                                    <option value="haiku">Haiku (fast)</option><option value="sonnet">Sonnet (better)</option><option value="opus">Opus (best, slow)</option>
                                </select></label>
                            <p className="text-gray-500">Runs on the AI Worker PC through its Claude login, like the lead suggestions.</p>
                        </div>
                    </div>
                </Section>

                <Section title="Facts we may collect"
                    subtitle="Only ticked facts are read from websites and shown to the writer. Everything is public and free.">
                    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4 text-xs">
                        {Object.entries(GROUPS).map(([g, label]) => (
                            <div key={g} className="space-y-1">
                                <p className="font-medium text-gray-900 dark:text-white">{label}</p>
                                {d.signal_catalog.filter(s => s.group === g).map(s => (
                                    <label key={s.code} title={s.help} className="flex items-start gap-1.5 text-gray-700 dark:text-gray-300">
                                        <input type="checkbox" className="mt-0.5" checked={confirmed.has(s.code)} onChange={() => toggleSignal(s.code)} />
                                        <span>{s.label}{s.weight ? <span className="text-gray-400"> · +{s.weight}</span> : null}</span>
                                    </label>
                                ))}
                            </div>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <Btn variant="ghost" onClick={() => set("confirmed_signals", d.signal_catalog.map(s => s.code))}>Tick all</Btn>
                        <Btn variant="ghost" onClick={() => set("confirmed_signals", [])}>Untick all</Btn>
                    </div>
                </Section>

                <Section title="Pace" subtitle="Start slow: 20-40 a day protects your domain's reputation. Each mailbox's own daily limit applies as well.">
                    <div className="grid gap-3 md:grid-cols-4 text-xs">
                        <label className="space-y-1"><span className="font-medium">Cold emails per day</span>
                            <input type="number" min={0} max={2000} value={d.daily_send_cap} onChange={e => set("daily_send_cap", Number(e.target.value) || 0)} className={inputCls} /></label>
                        <label className="space-y-1"><span className="font-medium">From hour</span>
                            <input type="number" min={0} max={23} value={d.send_hour_start} onChange={e => set("send_hour_start", Number(e.target.value))} className={inputCls} /></label>
                        <label className="space-y-1"><span className="font-medium">Until hour</span>
                            <input type="number" min={1} max={24} value={d.send_hour_end} onChange={e => set("send_hour_end", Number(e.target.value))} className={inputCls} /></label>
                        <label className="space-y-1"><span className="font-medium">Days between emails</span>
                            <input value={d.step_gaps.join(", ")} onChange={e => set("step_gaps", e.target.value.split(",").map(x => Number(x.trim())).filter(Boolean))} className={inputCls} /></label>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 text-xs">
                        <span className="font-medium">Days</span>
                        {DAYS.map((label, i) => (
                            <label key={label} className="flex items-center gap-1">
                                <input type="checkbox" checked={d.send_days.includes(i)}
                                    onChange={() => set("send_days", d.send_days.includes(i) ? d.send_days.filter(x => x !== i) : [...d.send_days, i].sort())} /> {label}
                            </label>
                        ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs">
                        <input type="checkbox" checked={d.allow_risky} onChange={e => set("allow_risky", e.target.checked)} />
                        Also email "risky" addresses (published, but the mail server could not be checked)
                    </label>
                </Section>

                <Suppressions />
            </div>
        </ContainerCard>
    );
}
